import * as React from "react";
import Editor from "react-simple-code-editor";

import { cva } from "class-variance-authority";
import { Files } from "lucide-react";
import Prism from "prismjs";
import "prismjs/components/prism-python";

import { cn } from "../../utils";
import { Button } from "./button";

export const CODE_EDITOR_LANGUAGES = ["python"] as const;

export type CodeEditorLanguage = (typeof CODE_EDITOR_LANGUAGES)[number];

const PRISM_LANGUAGE_ALIAS: Record<CodeEditorLanguage, string> = {
  python: "python",
};

const codeEditorVariants = cva(
  "af-relative af-w-full af-overflow-auto af-rounded-md af-border af-border-input af-bg-muted af-font-code af-ring-offset-background focus-within:af-ring-2 focus-within:af-ring-ring focus-within:af-ring-offset-2",
  {
    variants: {
      disabled: {
        true: "af-pointer-events-none af-cursor-not-allowed af-opacity-50",
      },
      error: {
        true: "af-border-destructive",
      },
    },
    defaultVariants: {
      disabled: false,
      error: false,
    },
  },
);

export interface CodeEditorProps extends Omit<
  React.ComponentProps<"div">,
  "onChange"
> {
  value: string;
  onChange?: (value: string) => void;
  language?: CodeEditorLanguage;
  readOnly?: boolean;
  disabled?: boolean;
  error?: boolean;
  placeholder?: string;
}

const LineNumbers = ({ count }: { count: number }) => (
  <div aria-hidden="true" className="af-text-muted-foreground">
    {Array.from({ length: count }, (_, i) => (
      <div key={i + 1}>{i + 1}</div>
    ))}
  </div>
);

export const CodeEditor = ({
  value,
  onChange,
  language = "python",
  readOnly = false,
  disabled = false,
  error = false,
  className,
  placeholder,
  id,
  ...props
}: CodeEditorProps) => {
  const prismLang = PRISM_LANGUAGE_ALIAS[language];
  const lineCount = Math.max(1, value.split("\n").length);

  const highlighted = React.useMemo(() => {
    const grammar = Prism.languages[prismLang];
    if (!grammar) return value;
    return Prism.highlight(value, grammar, prismLang);
  }, [value, prismLang]);

  const handleHighlight = () => highlighted;

  const handleCopy = () => {
    void navigator.clipboard.writeText(value);
  };

  return (
    <div
      data-slot="code-editor"
      className={cn(
        codeEditorVariants({ disabled, error }),
        "af-flex af-h-full af-flex-col",
        className,
      )}
      {...props}
    >
      <div className="af-flex af-items-center af-justify-between af-border-b af-border-input af-px-3 af-py-0.5 af-text-xs af-text-muted-foreground">
        <span className="af-capitalize">{language}</span>
        <Button
          size="icon-xs"
          variant="ghost"
          type="button"
          onClick={handleCopy}
        >
          <Files className="af-size-3" />
        </Button>
      </div>
      <div className="af-flex af-min-h-0 af-min-w-full af-flex-1 af-gap-2 af-px-3 af-py-2 af-text-sm">
        <LineNumbers count={lineCount} />
        <div className="af-flex af-min-w-0 af-flex-1 af-flex-col">
          <Editor
            value={value}
            onValueChange={
              disabled || readOnly
                ? () => undefined
                : (onChange ?? (() => undefined))
            }
            highlight={handleHighlight}
            disabled={disabled}
            readOnly={readOnly}
            placeholder={placeholder}
            textareaId={id}
            textareaClassName="af-outline-none"
            style={{ height: "100%" }}
          />
        </div>
      </div>
    </div>
  );
};

CodeEditor.displayName = "CodeEditor";
